import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, flattenFilterConditions, isDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MissingListQuery } from './types/missing-list-query.type';
import type { MissingListsPagedData } from './types/missing-lists-paged-data.type';

/**
 * Fetches a paged set of brokerage-wide missing mailing lists from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param searchParams - The query parameters for paging, filtering, and the
 *   Missing-Lists toolbar (search / status).
 * @returns A promise that resolves to an operation result containing the paged
 *   missing-lists envelope, including the sibling status-count fields.
 * @throws Will throw an error if the request fails.
 */
export const getMissingLists = async (instance: KyInstance, searchParams: MissingListQuery) => {
  try {
    const { filter, sort, ...restParams } = searchParams;
    const flattenedFilters = isDefined(filter) ? flattenFilterConditions(filter) : [];

    return await instance
      .get<OperationResultWithData<MissingListsPagedData>>(Uri.mailingList.getMissingLists, {
        searchParams: {
          ...restParams,
          ...convertToQueryString(flattenedFilters, 'filter'),
          ...convertToQueryString(sort, 'sort'),
        },
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
